import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { getLocale, history, setLocale } from '@umijs/max';
import { message } from 'antd';
import React from 'react';
import { AvatarDropdown, AvatarName, SelectLang } from '@/components';
import IdleLogout from '@/components/IdleLogout';
import { renderSidebarMenuItem } from '@/layout/sidebarMenuItemRender';
import { meApi } from '@/services/auth';
import { resolveApiErrorMessage } from '@/utils/apiError';
import {
  extractApiFieldErrors,
  formatApiValidationMessage,
} from '@/utils/apiValidation';
import { getToken } from '@/utils/auth';
import { AuthAction, classifyAuthError } from '@/utils/authErrorHandler';
import { doLogout } from '@/utils/authLogout';
import {
  normalizeAppLocale,
  toAppLocaleFromLangKey,
  toLangKeyFromLocale,
} from '@/utils/locale';
import { filterMenuDataByPermissions } from '@/utils/menuPermissions';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/login';

const publicPaths = new Set([
  '/login',
  '/activate',
  '/setup',
  '/forget-password',
  '/reset-password',
  '/password-expired',
]);

type AppInitialState = {
  currentUser?: any;
  settings?: Record<string, any>;
};

function readPersistedLocale(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const stored = window.localStorage?.getItem?.('umi_locale');
  return stored ? normalizeAppLocale(stored) : undefined;
}

export async function getInitialState(): Promise<AppInitialState> {
  const currentLocale = normalizeAppLocale(getLocale?.());
  if (getLocale?.() !== currentLocale) {
    setLocale(currentLocale, false);
  }

  const token = getToken();
  if (!token) return {};
  try {
    const currentUser = await meApi();
    const userLocale = toAppLocaleFromLangKey(currentUser?.langKey);
    const persistedLocale = readPersistedLocale();
    const effectiveLocale = persistedLocale ?? userLocale;
    if (effectiveLocale !== currentLocale) {
      setLocale(effectiveLocale, false);
    }
    return { currentUser };
  } catch {
    return {};
  }
}

export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    logo: '/logo.svg',
    actionsRender: () => [<SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu>{avatarChildren}</AvatarDropdown>;
      },
    },
    menuDataRender: (menuData: any[]) =>
      filterMenuDataByPermissions(
        menuData,
        initialState?.currentUser?.permissions,
      ),
    menuItemRender: renderSidebarMenuItem,
    onPageChange: () => {
      const { location } = history;
      const token = getToken();
      const isPublicReceiptPath =
        location.pathname.startsWith('/r/') ||
        location.pathname.startsWith('/batch-r/');
      if (
        !token &&
        !publicPaths.has(location.pathname) &&
        !isPublicReceiptPath
      ) {
        history.push(loginPath);
        return;
      }
      // Redirect from root to dashboard
      if (location.pathname === '/') {
        history.push('/dashboard');
      }
    },
    menuHeaderRender: undefined,
    childrenRender: (children) => {
      return (
        <>
          <IdleLogout />
          <div
            style={{
              animation: 'fadeIn 0.3s ease-out',
            }}
          >
            {children}
          </div>
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState(
                  (preInitialState) =>
                    ({
                      ...preInitialState,
                      settings,
                    }) as AppInitialState,
                );
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

export const request: RequestConfig = {
  timeout: 15000,
  baseURL: process.env.UMI_APP_API_BASE_URL,
  requestInterceptors: [
    (url, options) => {
      const token = getToken();
      const locale = normalizeAppLocale(getLocale?.());
      const authorization = token
        ? token.startsWith('Bearer ')
          ? token
          : `Bearer ${token}`
        : undefined;
      const headers = {
        ...options.headers,
        'Accept-Language': toLangKeyFromLocale(locale),
        ...(authorization ? { Authorization: authorization } : {}),
      };
      return { url, options: { ...options, headers } };
    },
  ],
  errorConfig: {
    errorHandler: (error: any, opts?: { skipErrorHandler?: boolean }) => {
      // Handle auth errors (401) — redirect to login
      const authResult = classifyAuthError(error);
      if (authResult.action === AuthAction.REDIRECT_LOGIN) {
        if (authResult.message) message.error(authResult.message);
        doLogout();
        return;
      }
      const fieldErrors = extractApiFieldErrors(error);
      if (fieldErrors.length > 0) {
        error.fieldErrors = fieldErrors;
      }
      // Resolve the business error message from API response body
      const resolved =
        formatApiValidationMessage(error) ||
        resolveApiErrorMessage(error, 'Request failed');
      // Overwrite generic axios message (e.g. "Request failed with status code 400")
      // with the actual API error message so all catch blocks get the right text.
      error.message = resolved;

      if (opts?.skipErrorHandler) {
        throw error;
      }
      // Show a single toast. Page-level catches should NOT show additional toasts —
      // they only catch to handle UI flow (e.g. keep modal open).
      message.error(resolved);
      throw error;
    },
  },
};
